/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  Address,
  Address$inboundSchema,
  Address$Outbound,
  Address$outboundSchema,
} from "./address.js";
import {
  BusinessType,
  BusinessType$inboundSchema,
  BusinessType$outboundSchema,
} from "./businesstype.js";
import {
  IndustryCodes,
  IndustryCodes$inboundSchema,
  IndustryCodes$Outbound,
  IndustryCodes$outboundSchema,
} from "./industrycodes.js";
import {
  PhoneNumber,
  PhoneNumber$inboundSchema,
  PhoneNumber$Outbound,
  PhoneNumber$outboundSchema,
} from "./phonenumber.js";
import {
  PrimaryRegulator,
  PrimaryRegulator$inboundSchema,
  PrimaryRegulator$outboundSchema,
} from "./primaryregulator.js";
import {
  Representative,
  Representative$inboundSchema,
  Representative$Outbound,
  Representative$outboundSchema,
} from "./representative.js";

/**
 * Describes a business.
 */
export type BusinessProfile = {
  /**
   * The legal name under which the entity is registered.
   */
  legalBusinessName: string;
  /**
   * A registered trade name under which the business operates, if different from its legal name.
   */
  doingBusinessAs?: string | undefined;
  /**
   * The type of entity represented by this business.
   */
  businessType?: BusinessType | undefined;
  address?: Address | undefined;
  phone?: PhoneNumber | undefined;
  email?: string | undefined;
  website?: string | undefined;
  description?: string | undefined;
  /**
   * Indicates whether a tax ID has been provided for this business.
   */
  taxIDProvided?: boolean | undefined;
  representatives?: Array<Representative> | undefined;
  ownersProvided: boolean;
  industryCodes?: IndustryCodes | undefined;
  /**
   * Classification identifier for the industry. Use the [GET industries](https://docs.moov.io/api/enrichment/form-shortening/industries/get/) endpoint to retrieve an array of valid industry details for a merchant, inducing all industry field values.
   */
  industry?: string | undefined;
  /**
   * If the business is a financial institution, this field describes its primary regulator.
   */
  primaryRegulator?: PrimaryRegulator | undefined;
};

/** @internal */
export const BusinessProfile$inboundSchema: z.ZodType<
  BusinessProfile,
  z.ZodTypeDef,
  unknown
> = z.object({
  legalBusinessName: z.string(),
  doingBusinessAs: z.string().optional(),
  businessType: BusinessType$inboundSchema.optional(),
  address: Address$inboundSchema.optional(),
  phone: PhoneNumber$inboundSchema.optional(),
  email: z.string().optional(),
  website: z.string().optional(),
  description: z.string().optional(),
  taxIDProvided: z.boolean().optional(),
  representatives: z.array(Representative$inboundSchema).optional(),
  ownersProvided: z.boolean(),
  industryCodes: IndustryCodes$inboundSchema.optional(),
  industry: z.string().optional(),
  primaryRegulator: PrimaryRegulator$inboundSchema.optional(),
});
/** @internal */
export type BusinessProfile$Outbound = {
  legalBusinessName: string;
  doingBusinessAs?: string | undefined;
  businessType?: string | undefined;
  address?: Address$Outbound | undefined;
  phone?: PhoneNumber$Outbound | undefined;
  email?: string | undefined;
  website?: string | undefined;
  description?: string | undefined;
  taxIDProvided?: boolean | undefined;
  representatives?: Array<Representative$Outbound> | undefined;
  ownersProvided: boolean;
  industryCodes?: IndustryCodes$Outbound | undefined;
  industry?: string | undefined;
  primaryRegulator?: string | undefined;
};

/** @internal */
export const BusinessProfile$outboundSchema: z.ZodType<
  BusinessProfile$Outbound,
  z.ZodTypeDef,
  BusinessProfile
> = z.object({
  legalBusinessName: z.string(),
  doingBusinessAs: z.string().optional(),
  businessType: BusinessType$outboundSchema.optional(),
  address: Address$outboundSchema.optional(),
  phone: PhoneNumber$outboundSchema.optional(),
  email: z.string().optional(),
  website: z.string().optional(),
  description: z.string().optional(),
  taxIDProvided: z.boolean().optional(),
  representatives: z.array(Representative$outboundSchema).optional(),
  ownersProvided: z.boolean(),
  industryCodes: IndustryCodes$outboundSchema.optional(),
  industry: z.string().optional(),
  primaryRegulator: PrimaryRegulator$outboundSchema.optional(),
});

export function businessProfileToJSON(
  businessProfile: BusinessProfile,
): string {
  return JSON.stringify(BusinessProfile$outboundSchema.parse(businessProfile));
}
export function businessProfileFromJSON(
  jsonString: string,
): SafeParseResult<BusinessProfile, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => BusinessProfile$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'BusinessProfile' from JSON`,
  );
}
